import { css } from "@emotion/react";
import { Children, FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, ShadowToken, SpacingToken } from "@livechat/design-system-react-components";

import { withVar } from "themes/helpers";

import { CAROUSEL, CAROUSEL_CHILD } from "./test-id-constants";

interface ICarouselProps {
  children: ReactNode;
  expandedItemIndex?: number;
}

const Carousel: FC<ICarouselProps> = ({ children, expandedItemIndex }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            updateArrows();
          }
        });
      },
      { root: container, threshold: 0.1 }
    );

    Array.from(container.children).forEach(child => {
      observer.observe(child);
    });

    return (): void => {
      observer.disconnect();
    };
  }, [children, updateArrows]);

  const scroll = (direction: "left" | "right"): void => {
    if (containerRef.current) {
      const scrollAmount = containerRef.current.clientWidth * 0.8;
      containerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div css={containerStyles} data-testid={CAROUSEL}>
      <div css={childrenContainerStyles} ref={containerRef} onScroll={updateArrows}>
        {Children.map(children, (child, index) => (
          <div
            css={{
              childContainerStyles,
              ...(expandedItemIndex !== undefined &&
                (expandedItemIndex === index ? selectedChildContainerStyles : unselectedChildContainerStyles)),
            }}
            data-testid={CAROUSEL_CHILD}
          >
            {child}
          </div>
        ))}
      </div>

      <div css={[leftArrowButtonContainerStyles, showLeftArrow ? visibleArrowStyles : hiddenArrowStyles]}>
        <Button
          kind="high-contrast"
          size="xcompact"
          icon={<Icon source={ChevronLeft} />}
          onClick={() => scroll("left")}
        />
      </div>

      <div css={[rightArrowButtonContainerStyles, showRightArrow ? visibleArrowStyles : hiddenArrowStyles]}>
        <Button
          kind="high-contrast"
          size="xcompact"
          icon={<Icon source={ChevronRight} />}
          onClick={() => scroll("right")}
        />
      </div>
    </div>
  );
};

export const containerStyles = css`
  position: relative;
  width: 100%;
  display: flex;
`;

export const childrenContainerStyles = css`
  display: flex;
  overflow-x: scroll;
  overflow-y: hidden;
  width: 100%;
  gap: ${withVar(SpacingToken.Spacing4)};
  -webkit-overflow-scrolling: touch;
  align-items: flex-start;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const childContainerStyles = css`
  flex-grow: 0;
  flex-shrink: 0;
`;

const selectedChildContainerStyles = css`
  width: 100%;
`;

const unselectedChildContainerStyles = css`
  display: none;
`;

export const arrowButtonContainerStyles = css`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const leftArrowButtonContainerStyles = css`
  ${arrowButtonContainerStyles}
  left: ${withVar(SpacingToken.Spacing2)};
`;

const rightArrowButtonContainerStyles = css`
  ${arrowButtonContainerStyles}
  right: ${withVar(SpacingToken.Spacing2)};
`;

const arrowButtonStyles = css`
  align-self: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  border: 0;
  box-shadow: ${withVar(ShadowToken.Float)};
  outline: none;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  &:hover {
    svg {
      fill: ${withVar(DesignToken.ActionPrimaryDefault)};
    }
  }
  svg {
    display: inline;
  }
`;

export const leftArrowButtonStyles = css`
  ${arrowButtonStyles}
  left: .5em;
`;

export const rightArrowButtonStyles = css`
  ${arrowButtonStyles}
  right: .5em;
`;

export const leftButtonContainerStyles = css`
  ${arrowButtonContainerStyles}
  left: 0;
`;

export const rightButtonContainerStyles = css`
  ${arrowButtonContainerStyles}
  right: 0;
`;

const visibleArrowStyles = css`
  opacity: 1;
  visibility: visible;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
`;

const hiddenArrowStyles = css`
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
`;

export default Carousel;
